import React, { useState,useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';

import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';

import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react'; // Optional: Use any grip icon library

import useFeatureUsageData from '../utilities/useFeatureUsageData';
import useUsedVsUnused from '../utilities/useUsedVsUnused';
import useLeadImportData from '../utilities/useLeadImportData';
import useImportToolUsage from '../utilities/useImportToolUsage';
import computeUsageChartData from '../utilities/computeUsageChartData.js';

import FeatureUsageChart from '../components/FeatureUsage/FeatureUsageChart';
import UsedVsUnused from '../components/FeatureUsage/UsedVsUnused';
import LeadImportChart from '../components/FeatureUsage/LeadImportChart';
import ImportToolUsage from '../components/FeatureUsage/ImportToolUsage';
import Navbar from '../components/navbar';


const systemPanels = ['lead-import-chart', 'used-vs-unused', 'import-tool-usage'];
const userPanels = ['usage-by-user'];

const panels = [
  { id: 'import-tool-usage', label: '📊 Import Tool Usage' },
  { id: 'lead-import-chart', label: '📥 Leads Imported by Source' },
  { id: 'used-vs-unused', label: '🧩 Used vs. Unused Features' },
  { id: 'usage-by-user', label: '📌 Usage by User' },
];

function SortablePanel({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group">
        <div
          {...attributes}
          {...listeners}
          className="absolute right-4 top-4 cursor-grab opacity-0 group-hover:opacity-100 transition"
          title="Drag to reorder"
        >
          <GripVertical className="w-4 h-4 text-gray-400 hover:text-white" />
        </div>
      {children}
    </div>
  );
}

export default function Analytics() {
  const { used, unused, loading: loadingUsed } = useUsedVsUnused();
  const { data: importToolData, loading: loadingImportTool } = useImportToolUsage();
  const [selectedSources, setSelectedSources] = useState(['PlanHub', 'ConstructConnect', 'Lead List', 'Project List']);
  
  const [importInterval, setImportInterval] = useState('daily');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedUser, setSelectedUser] = useState('all');
  const [displayMode, setDisplayMode] = useState('all'); // 'all', 'imported', 'updated'

  const [usageInterval,setUsageInterval]   = useState('daily');
  const [usageStart,setUsageStart]         = useState('');
  const [usageEnd,setUsageEnd]             = useState('');
  const [usageSelectedUser,setUsageSelectedUser] = useState('all');
  const [selectedFeature, setSelectedFeature] = useState('all');
  const [selectedPageView, setSelectedPageView] = useState('all')

  const {
    users,
    features,
    periods,
    dataByUser: usageDataByUser,
    loading: loadingUsage
  } = useFeatureUsageData({
    interval:  usageInterval,
    startDate: usageInterval === 'custom' ? usageStart : undefined,
    endDate:   usageInterval === 'custom' ? usageEnd   : undefined
  });

  const {
    users: importUsers,
    features: importFeatures,
    periods: importPeriods,
    dataByUser,
    loading: loadingImportChart
  } = useLeadImportData({
    interval: importInterval,
    startDate: importInterval === 'custom' ? startDate : undefined,
    endDate:   importInterval === 'custom' ? endDate   : undefined,
  });


   // derive top-level vs page-view options
   const pageViewOptions = features.filter(f => f.startsWith('Page View -') && f !== 'Page View - All')
   const mainFeatures = Array.from(new Set(
     features.map(f => f.startsWith('Page View -') ? 'Page View' : f)
   ))
  
  const chartData = useMemo(() => {
    if (!importUsers || !importUsers.length || !importFeatures || !importFeatures.length) return [];

    // All users = aggregate across them
    if (selectedUser === 'all') {
      return importPeriods.map(period => {
        const row = { period };
        importFeatures.forEach(feature => {
          const totalCounts = { imported: 0, updated: 0 };
          
          importUsers.forEach(user => {
            if (!dataByUser || !dataByUser[user]) return;
            
            const entry = dataByUser[user]?.find(r => r?.period === period);
            if (!entry) return;
            
            const value = entry[feature];
            
            // Handle object-based data (with imported/updated properties)
            if (typeof value === 'object' && value !== null) {
              totalCounts.imported += (value.imported || 0);
              totalCounts.updated += (value.updated || 0);
            } 
            // Handle legacy number data
            else if (typeof value === 'number' || typeof value === 'string') {
              totalCounts.imported += Number(value) || 0;
            }
          });
          
          // If we have detailed counts, use them
          if (totalCounts.imported > 0 || totalCounts.updated > 0) {
            row[feature] = {
              imported: totalCounts.imported,
              updated: totalCounts.updated
            };
          } else {
            // Fallback to simple sum for backward compatibility
            row[feature] = importUsers.reduce((sum, user) => {
              if (!dataByUser || !dataByUser[user]) return sum;
              const entry = dataByUser[user]?.find(r => r?.period === period);
              const value = entry?.[feature];
              return sum + (typeof value === 'number' ? value : 0);
            }, 0);
          }
        });
        return row;
      });
    }

    return dataByUser?.[selectedUser] || [];
  }, [importUsers, importFeatures, importPeriods, dataByUser, selectedUser]);


  // Totals for pills
  const totalLeadsBySource = useMemo(() => {
    if (!chartData || !importFeatures) return {};
    
    return chartData.reduce((acc, row) => {
      if (!row) return acc;
      
      importFeatures.forEach(f => {
        // Check if we have detailed data with imported/updated counts
        const value = row[f];
        if (typeof value === 'object' && value !== null) {
          acc[f] = acc[f] || { total: 0, imported: 0, updated: 0 };
          acc[f].imported += (value.imported || 0);
          acc[f].updated += (value.updated || 0);
          acc[f].total += (value.imported || 0) + (value.updated || 0);
        } else {
          // Legacy data without detailed counts
          acc[f] = acc[f] || { total: 0, imported: 0, updated: 0 };
          acc[f].total += (value || 0);
          acc[f].imported += (value || 0); // Assume all are imported for backward compatibility
        }
      });
      return acc;
    }, {});
  }, [chartData, importFeatures]);

  // Calculate overall totals
  const overallTotals = useMemo(() => {
    if (!totalLeadsBySource) {
      return { total: 0, imported: 0, updated: 0 };
    }
    
    return Object.values(totalLeadsBySource).reduce(
      (acc, src) => {
        if (!src) return acc;
        acc.total += src.total || 0;
        acc.imported += src.imported || 0;
        acc.updated += src.updated || 0;
        return acc;
      },
      { total: 0, imported: 0, updated: 0 }
    );
  }, [totalLeadsBySource]);

  const [order, setOrder] = useState(panels.map(p => p.id));
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = order.indexOf(active.id);
      const newIndex = order.indexOf(over.id);
      setOrder(arrayMove(order, oldIndex, newIndex));
    }
  };

  const toggleSource = source =>
    setSelectedSources(prev =>
      prev.includes(source)
        ? prev.filter(s => s !== source)
        : [...prev, source]
    );

  const panelContent = {
'usage-by-user': (
  <section className="bg-gray-900 rounded-2xl shadow-md border border-white/10 p-6 space-y-6">
      <header className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-2">
        <div>
          <h2 className="text-2xl font-bold text-white">📌 Usage by User</h2>
          <p className="text-sm text-gray-300 mt-1">
            How frequently each team member engages with specific features.
          </p>
        </div>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-start gap-6">
        {/* User */}
        <div className="flex items-center gap-2 mb-2 sm:mb-0">
          <label htmlFor="userFilter" className="text-sm text-gray-300">User:</label>
          <select
            id="userFilter"
            value={usageSelectedUser}
            onChange={e => setUsageSelectedUser(e.target.value)}
            className="bg-gray-800 text-white border border-gray-600 px-2 py-1 rounded"
          >
            <option value="all">All Users</option>
            {users.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>

        {/* Feature */}
        <div className="flex items-center gap-2 mb-2 sm:mb-0">
          <label htmlFor="featureFilter" className="text-sm text-gray-300">Feature:</label>
          <select
            id="featureFilter"
            value={selectedFeature}
            onChange={e => {
              setSelectedFeature(e.target.value)
              if (e.target.value !== 'Page View') {
                setSelectedPageView('all')
              }
            }}
            className="bg-gray-800 text-white border border-gray-600 px-2 py-1 rounded"
          >
            <option value="all">All Features</option>
            {mainFeatures.map(f => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </div>

        {/* Page-View Subtype */}
        {selectedFeature === 'Page View' && (
          <div className="flex items-center gap-2 mb-2 sm:mb-0">
            <label htmlFor="pageViewSubFilter" className="text-sm text-gray-300">Page:</label>
            <select
              id="pageViewSubFilter"
              value={selectedPageView}
              onChange={e => setSelectedPageView(e.target.value)}
              className="bg-gray-800 text-white border border-gray-600 px-2 py-1 rounded"
            >
              <option value="all">All Pages</option>
              {pageViewOptions.map(pv => (
                <option key={pv} value={pv}>
                  {pv.replace('Page View - ', '')}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Interval + Dates */}
        <div className="flex flex-row gap-2">
          <div className="flex items-center gap-2">
            <label htmlFor="usageInterval" className="text-sm text-gray-300">Interval:</label>
            <select
              id="usageInterval"
              value={usageInterval}
              onChange={e => {
                setUsageInterval(e.target.value)
                if (e.target.value !== 'custom') {
                  setUsageStart('')
                  setUsageEnd('')
                }
              }}
              className="bg-gray-800 text-white border border-gray-600 px-2 py-1 rounded"
            >
              {['daily','weekly','monthly','custom'].map(opt => (
                <option key={opt} value={opt}>
                  {opt.charAt(0).toUpperCase() + opt.slice(1)}
                </option>
              ))}
            </select>
          </div>
          {usageInterval === 'custom' && (
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-1">
                <label className="text-sm text-gray-300">From:</label>
                <input
                  type="date"
                  value={usageStart}
                  onChange={e => setUsageStart(e.target.value)}
                  className="bg-gray-800 text-white border border-gray-600 px-2 py-1 rounded w-32"
                />
              </div>
              <div className="flex items-center gap-1">
                <label className="text-sm text-gray-300">To:</label>
                <input
                  type="date"
                  value={usageEnd}
                  onChange={e => setUsageEnd(e.target.value)}
                  className="bg-gray-800 text-white border border-gray-600 px-2 py-1 rounded w-32"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      <div className="w-full h-[360px]">
        {loadingUsage
          ? <div className="text-center text-gray-400 animate-pulse pt-16">
              Loading user activity…
            </div>
          : <FeatureUsageChart
              data={computeUsageChartData({
                dataByUser:      usageDataByUser,
                periods,
                users,
                features,
                selectedUser:    usageSelectedUser,
                selectedFeature,
                selectedPageView
              })}
            />
        }
      </div>
    </section>
),
'lead-import-chart': (
  <section className="bg-gray-900 rounded-2xl shadow-md border border-white/10 p-6 space-y-6">
    {/* Header */}
    <header className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-2">
      <div>
        <h2 className="text-2xl font-bold text-white">
          {displayMode === 'all' && "📥 Total Leads Imported by Source"}
          {displayMode === 'imported' && "🆕 New Contact Imports by Source"}
          {displayMode === 'updated' && "🔄 Contact Updates by Source"}
        </h2>
        <p className="text-sm text-gray-400 mt-1">
          {displayMode === 'all' && "Total Leads Imported to HubSpot:"}
          {displayMode === 'imported' && "New Contacts Imported to HubSpot:"}
          {displayMode === 'updated' && "Contacts Updated in HubSpot:"}
          &nbsp;
          <span className="text-white font-semibold">
            {displayMode === 'all' && overallTotals.total.toLocaleString()}
            {displayMode === 'imported' && overallTotals.imported.toLocaleString()}
            {displayMode === 'updated' && overallTotals.updated.toLocaleString()}
          </span>
          {displayMode === 'all' && (overallTotals.imported > 0 || overallTotals.updated > 0) && (
            <span className="text-gray-400 ml-2">
              ({overallTotals.imported.toLocaleString()} new, {overallTotals.updated.toLocaleString()} updated)
            </span>
          )}
        </p>
      </div>
    </header>

      {/* Filters Container */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:gap-6">
        {/* User Filter */}
        <div className="flex items-center gap-2 mb-2 sm:mb-0">
          <label htmlFor="userFilter" className="text-sm text-gray-300">User:</label>
          <select
            id="userFilter"
            value={selectedUser}
            onChange={e => setSelectedUser(e.target.value)}
            className="bg-gray-800 text-white border border-gray-600 px-2 py-1 rounded"
          >
            <option value="all">All Users</option>
            {importUsers.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>

        {/* Display Mode Filter */}
        <div className="flex items-center gap-2 mb-2 sm:mb-0">
          <label htmlFor="displayMode" className="text-sm text-gray-300">Show:</label>
          <select
            id="displayMode"
            value={displayMode}
            onChange={e => setDisplayMode(e.target.value)}
            className="bg-gray-800 text-white border border-gray-600 px-2 py-1 rounded"
          >
            <option value="all">All Activity</option>
            <option value="imported">New Contacts</option>
            <option value="updated">Updated Contacts</option>
          </select>
        </div>

        {/* Interval + Custom Range */}
        <div className="flex flex-row gap-4">
          <div className="flex items-center gap-4">
            <label htmlFor="interval" className="text-sm text-gray-300">Interval:</label>
            <select
              id="interval"
              value={importInterval}
              onChange={e => {
                setImportInterval(e.target.value);
                if (e.target.value !== 'custom') {
                  setStartDate('');
                  setEndDate('');
                }
              }}
              className="bg-gray-800 text-white border border-gray-600 px-2 py-1 rounded"
            >
              {['daily','weekly','monthly','custom'].map(opt => (
                <option key={opt} value={opt}>
                  {opt.charAt(0).toUpperCase() + opt.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {importInterval === 'custom' && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <label className="text-sm text-gray-300">From:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className="bg-gray-800 text-white border border-gray-600 px-2 py-1 rounded  w-32"
                />
              </div>
              <div className="flex items-center gap-1">
                <label className="text-sm text-gray-300">To:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className="bg-gray-800 text-white border border-gray-600 px-2 py-1 rounded  w-32"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Source Pills */}
      <div className="flex flex-wrap gap-2">
        {importFeatures && importFeatures.length > 0 ? importFeatures.map(source => {
          if (!source) return null;
          
          const isActive = selectedSources.includes(source);
          const sourceData = totalLeadsBySource?.[source] || { total: 0, imported: 0, updated: 0 };
          
          // Calculate the display value based on the selected mode
          let displayValue = 0;
          if (displayMode === 'all') {
            displayValue = sourceData.total;
          } else if (displayMode === 'imported') {
            displayValue = sourceData.imported;
          } else if (displayMode === 'updated') {
            displayValue = sourceData.updated;
          }
          
          return (
            <label
              key={source}
              className={
                `flex items-center gap-2 px-4 py-2 rounded-full border font-medium text-sm transition ` +
                (isActive
                  ? 'bg-hs-blue border-hs-blue text-white'
                  : 'bg-gray-800 border-gray-600 text-gray-300 hover:border-gray-400')
              }
            >
              <input
                type="checkbox"
                checked={isActive}
                onChange={() => toggleSource(source)}
                className="hidden"
              />
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{source}</span>
                  <span className="bg-gray-700 text-white text-xs px-2 py-0.5 rounded-full">
                    {displayValue.toLocaleString()}
                  </span>
                </div>
                
                {displayMode === 'all' && (sourceData.imported > 0 || sourceData.updated > 0) && (
                  <div className="text-xs text-gray-300 mt-1">
                    {sourceData.imported > 0 && (
                      <span className="text-green-300">{sourceData.imported} new</span>
                    )}
                    {sourceData.imported > 0 && sourceData.updated > 0 && (
                      <span className="mx-1">•</span>
                    )}
                    {sourceData.updated > 0 && (
                      <span className="text-blue-300">{sourceData.updated} upd</span>
                    )}
                  </div>
                )}
              </div>
            </label>
          );
        }) : (
          <div className="text-gray-400 py-2">No sources available</div>
        )}
      </div>

      {/* Chart */}
      <div className="w-full h-[300px]">
        {loadingImportChart ? (
          <div className="text-center text-gray-400 animate-pulse pt-16">
            Loading import stats...
          </div>
        ) : chartData && chartData.length > 0 ? (
          <LeadImportChart
            data={chartData}
            visibleSources={selectedSources || []}
            dateRange={
              importInterval === 'custom'
                ? { start: startDate, end: endDate }
                : null
            }
            displayMode={displayMode}
          />
        ) : (
          <div className="text-center text-gray-400 pt-16">
            No data available for this interval.
          </div>
        )}
      </div>
    </section>
),
'used-vs-unused': (
  <section className="bg-gray-900 rounded-2xl shadow-md px-4 py-5 sm:p-6 lg:p-8 border border-white/10">
    <h2 className="text-2xl font-bold text-white mb-1">🧩 Used vs. Unused Features</h2>
    <p className="text-sm text-gray-300 mb-4">See which features are actively used and which are idle.</p>
    {loadingUsed ? (
      <div className="text-center text-gray-400 animate-pulse pt-10">Analyzing feature usage...</div>
    ) : (
      <UsedVsUnused used={used} unused={unused} />
    )}
  </section>
),
'import-tool-usage': (
  <section className="bg-gray-900 rounded-2xl shadow-md px-4 py-5 sm:p-6 lg:p-8 border border-white/10">
    <h2 className="text-2xl font-bold text-white mb-1">📊 Import Tool Usage</h2>
    <p className="text-sm text-gray-300 mb-4">Track which users are using the import tools and what types of imports they're performing.</p>
    {loadingImportTool ? (
      <div className="text-center text-gray-400 animate-pulse pt-10">Loading import tool usage data...</div>
    ) : (
      <ImportToolUsage data={importToolData} />
    )}
  </section>
),
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-900  text-white">
      <Navbar />
      <main className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-10">
      <header className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight text-white">📊 Feature Usage Dashboard</h1>
          <p className="text-gray-300 mt-2 text-lg">
            Visual insights into feature engagement and team behavior.
          </p>
        </div>
        </header>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div className="space-y-12">
            {/* System Analytics Section */}
            <section>
              <h2 className="text-3xl font-bold mb-4 text-white">System Analytics</h2>
              <SortableContext items={order.filter(id => systemPanels.includes(id))} strategy={verticalListSortingStrategy}>
                <div className="space-y-8">
                  {order.filter(id => systemPanels.includes(id)).map(id => (
                    <SortablePanel key={id} id={id}>
                      {panelContent[id]}
                    </SortablePanel>
                  ))}
                </div>
              </SortableContext>
            </section>

            {/* User Analytics Section */}
            <section>
              <h2 className="text-3xl font-bold mb-4 text-white">User Analytics</h2>
              <SortableContext items={order.filter(id => userPanels.includes(id))} strategy={verticalListSortingStrategy}>
                <div className="space-y-8">
                  {order.filter(id => userPanels.includes(id)).map(id => (
                    <SortablePanel key={id} id={id}>
                      {panelContent[id]}
                    </SortablePanel>
                  ))}
                </div>
              </SortableContext>
            </section>
          </div>
        </DndContext>
      </main>
    </div>
  );
}
